/**
 * errorLog — клиентское логирование ошибок в Supabase (P0-5, 1.31.0)
 *
 * Зачем:
 * Перед пилотами нам нужно видеть падения на устройствах реальных
 * пользователей. Раньше ошибка уходила в console.error на телефоне
 * техника и терялась. Теперь best-effort пишем её в таблицу
 * public.error_logs (см. миграцию 20260611000001_error_logs.sql).
 *
 * Принципы:
 *  • Best-effort. Логирование НИКОГДА не должно ломать приложение или
 *    кидать собственную ошибку. Любой сбой внутри logError проглатывается.
 *  • НЕ через outbox. Ошибки не кладутся в офлайн-очередь синка: это
 *    раздуло бы её и могло зациклить («ошибка про отправку ошибки»).
 *    Если сети нет — лог просто не уйдёт, и это приемлемо.
 *  • Анти-флуд. Один и тот же текст ошибки не шлём чаще раза в 10 сек,
 *    и не больше N логов за сессию — чтобы цикл рендера с ошибкой не
 *    отправил тысячу строк.
 *  • Без утечек. В path кладём только pathname (без query), чтобы не
 *    утёк recovery-токен или другой секрет из URL.
 *
 * Sentry (на будущее):
 * Если когда-нибудь подключим Sentry — точка входа здесь же. Читаем
 * VITE_SENTRY_DSN; пока он пуст, работает только Supabase-путь.
 */

import { supabase, isSupabaseConfigured } from '../domains/auth/supabaseClient';
import { useAuthStore } from '../domains/auth/authStore';

type ErrorSource = 'react' | 'window' | 'promise' | 'manual';

interface LogErrorInput {
  source: ErrorSource;
  message: string;
  stack?: string | null;
  context?: Record<string, unknown> | null;
}

// ─── Анти-флуд состояние (на время жизни вкладки) ──────────────────────────
const MAX_LOGS_PER_SESSION = 25;
const DEDUPE_WINDOW_MS = 10_000;

let sessionLogCount = 0;
const recentMessages = new Map<string, number>(); // message -> last sent ts

function shouldSend(message: string): boolean {
  if (sessionLogCount >= MAX_LOGS_PER_SESSION) return false;

  const now = Date.now();
  const last = recentMessages.get(message);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;

  recentMessages.set(message, now);
  // Лёгкая чистка старых записей, чтобы Map не рос бесконечно.
  if (recentMessages.size > 50) {
    for (const [key, ts] of recentMessages) {
      if (now - ts > DEDUPE_WINDOW_MS) recentMessages.delete(key);
    }
  }
  return true;
}

// Безопасно достаём контекст текущего пользователя из authStore (если есть).
function currentUserContext(): {
  company_id: string | null;
  user_id: string | null;
  user_role: string | null;
} {
  try {
    const user = useAuthStore.getState().user;
    if (!user) return { company_id: null, user_id: null, user_role: null };
    // user.role: UserRole = CompanyRole = 'technician' | 'dispatcher' |
    // 'company_admin' — ровно те значения, что разрешены в CHECK error_logs.
    return {
      company_id: user.company_id ?? null,
      user_id: user.id ?? null,
      user_role: user.role ?? null,
    };
  } catch {
    return { company_id: null, user_id: null, user_role: null };
  }
}

function safePath(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    // Только pathname + hash-маршрут БЕЗ query — чтобы не утёк токен из ?query.
    // Для recovery-токена он в hash после '#', но в hash может быть и
    // SPA-маршрут; берём только до '?' внутри hash на всякий случай.
    const { pathname, hash } = window.location;
    const safeHash = hash ? hash.split('?')[0] : '';
    return `${pathname}${safeHash}`;
  } catch {
    return null;
  }
}

/**
 * Санитайзер чувствительных данных перед отправкой в error_logs.
 *
 * Цель: предотвратить утечку токенов/паролей через stack/context/message.
 * Хотя error_logs читает только company_admin своей компании, эти данные
 * всё равно не должны там лежать — в случае компрометации админ-аккаунта
 * атакующий получит чистые токены/пароли.
 *
 * Маскируем:
 *   • JWT-подобные строки (3 base64-блока через точки, начинающиеся с eyJ)
 *   • Bearer-токены в заголовках
 *   • Значения полей password в JSON/query-форматах
 *   • Supabase API-ключи (sb_secret_... / sb_publishable_...)
 *
 * Сохраняем структуру и читаемость — заменяем только сами секреты, не
 * всю строку. Если ничего подозрительного нет — строка возвращается
 * без изменений.
 */
function sanitize(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    // JWT: eyJ...header.payload.signature
    .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[JWT_REDACTED]')
    // Bearer-токен
    .replace(/Bearer\s+[A-Za-z0-9._-]{20,}/gi, 'Bearer [REDACTED]')
    // password=xxx или "password":"xxx" или 'password': 'xxx'
    .replace(/(["']?password["']?\s*[:=]\s*["']?)[^"',\s}&]+/gi, '$1[REDACTED]')
    // Supabase service/anon ключи: sb_secret_... или sb_publishable_...
    .replace(/\b(sb_(?:secret|publishable)_[A-Za-z0-9_-]{20,})\b/g, '[SUPABASE_KEY_REDACTED]');
}

/**
 * Применяет sanitize рекурсивно к строкам внутри произвольного значения.
 * Объекты/массивы обходятся, остальные типы возвращаются как есть.
 */
function sanitizeDeep(value: unknown): unknown {
  if (typeof value === 'string') return sanitize(value);
  if (Array.isArray(value)) return value.map(sanitizeDeep);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = sanitizeDeep(v);
    }
    return out;
  }
  return value;
}

/**
 * Основная функция. Никогда не бросает. Возвращает Promise<void>, который
 * всегда резолвится (даже при ошибке отправки).
 */
export async function logError(input: LogErrorInput): Promise<void> {
  try {
    if (!isSupabaseConfigured || !supabase) return;

    const message = sanitize((input.message || 'Unknown error').slice(0, 2000)) ?? 'Unknown error';
    if (!shouldSend(message)) return;

    sessionLogCount += 1;

    const ctx = currentUserContext();

    const row = {
      company_id: ctx.company_id,
      user_id: ctx.user_id,
      user_role: ctx.user_role,
      source: input.source,
      message,
      stack: input.stack ? sanitize(String(input.stack).slice(0, 8000)) : null,
      path: safePath(),
      app_version:
        (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null,
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
      context: input.context ? (sanitizeDeep(input.context) as Record<string, unknown>) : null,
      client_timestamp: new Date().toISOString(),
    };

    // Намеренно НЕ await-им в вызывающем коде критически; но здесь await
    // нужен, чтобы проглотить возможный reject. Ошибку вставки игнорируем.
    const { error } = await supabase.from('error_logs').insert(row);
    if (error) {
      // Не эскалируем — только в консоль для локальной отладки.
      // eslint-disable-next-line no-console
      console.warn('[errorLog] insert failed:', error.message);
    }
  } catch (e) {
    // Логирование не должно ломать приложение ни при каких условиях.
    // eslint-disable-next-line no-console
    console.warn('[errorLog] swallowed:', e);
  }
}

/**
 * Устанавливает глобальные перехватчики ошибок окна:
 *  • window.onerror — синхронные необработанные ошибки
 *  • unhandledrejection — необработанные reject промисов
 *
 * Вызывается один раз при старте приложения (см. main.tsx).
 * Возвращает функцию очистки (снять слушатели), хотя на практике живёт
 * всю сессию.
 */
export function installGlobalErrorHandlers(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onError = (event: ErrorEvent) => {
    void logError({
      source: 'window',
      message: event.message || 'window.onerror',
      stack: event.error?.stack ?? null,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    void logError({
      source: 'promise',
      message,
      stack: reason instanceof Error ? reason.stack ?? null : null,
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
